import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Textarea from './Textarea'
import Select from './Select'
import {
  Mail,
  Phone,
  MessageSquare,
  Video,
  User,
  Calendar,
  Plus,
  X,
  Clock,
  ArrowDownRight,
  ArrowUpRight,
} from 'lucide-react'
import { formatDate } from '../utils/helpers'

const COMMUNICATION_TYPES = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'video_call', label: 'Video Call', icon: Video },
  { value: 'in_person', label: 'In Person', icon: User },
  { value: 'other', label: 'Other', icon: MessageSquare },
]

const ClientCommunications = ({ clientId }) => {
  const [communications, setCommunications] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    type: 'email',
    direction: 'outbound',
    subject: '',
    content: '',
    duration_minutes: '',
    outcome: '',
    scheduled_at: '',
  })
  const organizationId = useOrganizationId()
  const { canWrite } = useOrganization()

  useEffect(() => {
    if (clientId && organizationId) {
      fetchCommunications()
    }
  }, [clientId, organizationId])

  const fetchCommunications = async () => {
    if (!clientId || !organizationId) return
    try {
      setLoading(true)
      // NOTE: We previously tried to join auth.users via a relationship alias here:
      // .select('*, created_by_user:created_by(email, raw_user_meta_data)')
      // This caused a PostgREST schema error because there is no table named "created_by";
      // it's just a UUID column referencing auth.users. For now we just select the base
      // record fields. If we want user details later, we should either:
      // - use an explicit relationship name, or
      // - fetch user info separately by created_by.
      const { data, error } = await supabase
        .from('client_communications')
        .select('*')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setCommunications(data || [])
    } catch (error) {
      console.error('Error fetching communications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!clientId || !organizationId) return
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const payload = {
        organization_id: organizationId,
        client_id: clientId,
        type: formData.type,
        direction: formData.direction,
        subject: formData.subject || null,
        content: formData.content || null,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        outcome: formData.outcome || null,
        scheduled_at: formData.scheduled_at || null,
        created_by: user?.id,
        completed_at: formData.scheduled_at ? null : new Date().toISOString(),
      }

      const { error } = await supabase.from('client_communications').insert(payload)
      if (error) throw error

      setFormData({
        type: 'email',
        direction: 'outbound',
        subject: '',
        content: '',
        duration_minutes: '',
        outcome: '',
        scheduled_at: '',
      })
      setShowForm(false)
      fetchCommunications()
    } catch (error) {
      console.error('Error creating communication:', error)
      alert(error.message || 'Failed to record communication')
    }
  }

  const getTypeIcon = (type) => {
    const typeOption = COMMUNICATION_TYPES.find((t) => t.value === type)
    return typeOption ? typeOption.icon : MessageSquare
  }

  if (loading) return <div className="text-sm text-gray-500">Loading communications...</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageSquare size={20} />
          Communication History
        </h3>
        {canWrite('clients') && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1"
          >
            <Plus size={16} />
            Record Communication
          </Button>
        )}
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card variant="soft" className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Record Communication</h4>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Type"
                    options={COMMUNICATION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  />
                  <Select
                    label="Direction"
                    options={[
                      { value: 'inbound', label: 'Inbound' },
                      { value: 'outbound', label: 'Outbound' },
                    ]}
                    value={formData.direction}
                    onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                  />
                </div>
                <Input
                  label="Subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Brief subject or topic"
                />
                <Textarea
                  label="Content/Notes"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Details of the communication..."
                  rows={3}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="number"
                    label="Duration (minutes)"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    placeholder="For calls"
                  />
                  <Input
                    type="datetime-local"
                    label="Scheduled At"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                  />
                </div>
                <Textarea
                  label="Outcome"
                  value={formData.outcome}
                  onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                  placeholder="Result or follow-up needed..."
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    Save
                  </Button>
                  <Button variant="secondary" type="button" onClick={() => setShowForm(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Communications List */}
      {communications.length === 0 ? (
        <Card variant="soft" className="p-6 text-center">
          <MessageSquare size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-500">No communications recorded yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {communications.map((comm) => {
            const TypeIcon = getTypeIcon(comm.type)
            const DirectionIcon = comm.direction === 'inbound' ? ArrowDownRight : ArrowUpRight
            return (
              <Card key={comm.id} variant="soft" className="p-4">
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      comm.direction === 'inbound' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                    }`}
                  >
                    <TypeIcon size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{comm.subject || 'No subject'}</span>
                        <DirectionIcon
                          size={14}
                          className={comm.direction === 'inbound' ? 'text-blue-600' : 'text-green-600'}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(comm.created_at)}</span>
                    </div>
                    {comm.content && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{comm.content}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {comm.duration_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {comm.duration_minutes} min
                        </span>
                      )}
                      {comm.scheduled_at && (
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          Scheduled: {formatDate(comm.scheduled_at)}
                        </span>
                      )}
                      {comm.outcome && (
                        <span className="text-gray-700 font-medium">Outcome: {comm.outcome}</span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ClientCommunications

